import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../services/api";
import axiosPrivate from "../../services/axios/axiosPrivate";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

/* GET ALL DEPARTMENTS */
export const fetchDepartments = createAsyncThunk(
  "departments/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get(api.departments.getAll);
      const data = response?.data?.data;
      return Array.isArray(data) ? data : data?.content || [];
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch departments"));
    }
  }
);

/* CREATE DEPARTMENT */
export const createDepartment = createAsyncThunk(
  "departments/create",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.post(api.departments.getAll, payload);
      return response?.data?.data ?? payload;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to create department"));
    }
  }
);

/* UPDATE DEPARTMENT */
export const updateDepartment = createAsyncThunk(
  "departments/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.put(
        `${api.departments.getById}${id}`,
        payload
      );
      return { id, ...(response?.data?.data || payload) };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to update department"));
    }
  }
);

/* DEACTIVATE DEPARTMENT: DELETE /departments/inactivate-department/:id */
export const deactivateDepartment = createAsyncThunk(
  "departments/deactivate",
  async (id, { rejectWithValue }) => {
    try {
      await axiosPrivate.delete(`${api.departments.deactivate}${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to deactivate department"));
    }
  }
);

/* ACTIVATE DEPARTMENT: PUT /departments/activate-department/:id */
export const activateDepartment = createAsyncThunk(
  "departments/activate",
  async (id, { rejectWithValue }) => {
    try {
      await axiosPrivate.put(`${api.departments.activate}${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to activate department"));
    }
  }
);

const initialState = {
  list: [],
  loading: false,
  error: null,
  createLoading: false,
  updateLoading: false,
  statusUpdatingId: null,
  actionError: null,
};

const departmentsSlice = createSlice({
  name: "departments",
  initialState,
  reducers: {
    clearDepartmentErrors(state) {
      state.error = null;
      state.actionError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDepartments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDepartments.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload || [];
      })
      .addCase(fetchDepartments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch departments";
      })

      .addCase(createDepartment.pending, (state) => {
        state.createLoading = true;
        state.actionError = null;
      })
      .addCase(createDepartment.fulfilled, (state, action) => {
        state.createLoading = false;
        const created = action.payload;
        if (created && created.id != null) {
          const exists = state.list.some((item) => String(item.id) === String(created.id));
          if (!exists) state.list.unshift(created);
        }
      })
      .addCase(createDepartment.rejected, (state, action) => {
        state.createLoading = false;
        state.actionError = action.payload || "Failed to create department";
      })

      .addCase(updateDepartment.pending, (state) => {
        state.updateLoading = true;
        state.actionError = null;
      })
      .addCase(updateDepartment.fulfilled, (state, action) => {
        state.updateLoading = false;
        const updated = action.payload;
        const index = state.list.findIndex((item) => String(item.id) === String(updated.id));
        if (index !== -1) state.list[index] = { ...state.list[index], ...updated };
      })
      .addCase(updateDepartment.rejected, (state, action) => {
        state.updateLoading = false;
        state.actionError = action.payload || "Failed to update department";
      })

      .addCase(deactivateDepartment.pending, (state, action) => {
        state.statusUpdatingId = action.meta.arg;
        state.actionError = null;
      })
      .addCase(deactivateDepartment.fulfilled, (state, action) => {
        state.statusUpdatingId = null;
        const department = state.list.find((item) => String(item.id) === String(action.payload));
        if (department) department.delete = true;
      })
      .addCase(deactivateDepartment.rejected, (state, action) => {
        state.statusUpdatingId = null;
        state.actionError = action.payload || "Failed to deactivate department";
      })

      .addCase(activateDepartment.pending, (state, action) => {
        state.statusUpdatingId = action.meta.arg;
        state.actionError = null;
      })
      .addCase(activateDepartment.fulfilled, (state, action) => {
        state.statusUpdatingId = null;
        const department = state.list.find((item) => String(item.id) === String(action.payload));
        if (department) department.delete = false;
      })
      .addCase(activateDepartment.rejected, (state, action) => {
        state.statusUpdatingId = null;
        state.actionError = action.payload || "Failed to activate department";
      });
  },
});

export const { clearDepartmentErrors } = departmentsSlice.actions;
export default departmentsSlice.reducer;
